import { FC } from 'react'

import {
  EmployeeBlocksEmployeeFragment,
  ResourceFieldDataFragment,
  ValueDataFragment,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import type LegalEntity from 'modules/core/store/models/LegalEntity'

import CustomFieldsForm from 'modules/custom_resources/components/CustomFields/CustomFieldsForm'
import { filterCustomResourceFieldsDataByUsageGroup } from 'modules/custom_resources/lib/utils'
import { BankInfo } from 'modules/employees/components/blocks/BankInfo'
import { ContactBlock } from 'modules/employees/components/blocks/Contact'
import { EmployeeAddressBlock } from 'modules/employees/components/blocks/EmployeeAddress'
import { Identification } from 'modules/employees/components/blocks/EmployeeIdentification'
import { FamilySituation } from 'modules/employees/components/blocks/FamilySituation'
import GeneralBlock from 'modules/employees/components/blocks/General/GeneralBlock'
import { PersonalSituation } from 'modules/employees/components/blocks/PersonalSituation'
import { TaxResidence } from 'modules/employees/components/blocks/TaxResidence'
import type Employee from 'modules/employees/store/models/Employee'
import { DataCollectionCategories } from 'modules/processes/domain'
import Warning from 'modules/tasks/components/TaskDetails/DataCollection/Warning'

interface Props {
  employee: Employee
  employeeData: EmployeeBlocksEmployeeFragment
  legalEntity: LegalEntity | null
  readonly: boolean
  isCurrent: boolean
  category: DataCollectionCategories
  canSeePersonalInfo: boolean
  canEditPersonalInfo: boolean
  customResourceFieldsData: ResourceFieldDataFragment[]
  customValuesData: ValueDataFragment[]
}

const EmployeeData: FC<Props> = ({
  employee,
  employeeData,
  legalEntity,
  readonly,
  isCurrent,
  category,
  canSeePersonalInfo,
  canEditPersonalInfo,
  customResourceFieldsData,
  customValuesData,
}) => {
  const frenchEmployee = legalEntity && legalEntity.country === 'fr'

  switch (category) {
    case DataCollectionCategories.address:
      return <EmployeeAddressBlock employee={employeeData} readonly={readonly} />
    case DataCollectionCategories.familySituation:
      return frenchEmployee ? (
        <FamilySituation employee={employeeData} readonly={readonly} />
      ) : (
        <Warning message={i18n.t('tasks.types.data_collection.details.forbidden_message_fr')} />
      )
    case DataCollectionCategories.fiscal_residence:
      return <TaxResidence employee={employeeData} isCurrent={isCurrent} />
    case DataCollectionCategories.bankInformation:
      return (
        <BankInfo
          employee={employeeData}
          canEditPersonalInfo={canEditPersonalInfo}
          canSeeBankInfo={canSeePersonalInfo}
        />
      )
    case DataCollectionCategories.emergency_contact:
      return <ContactBlock employee={employeeData} readonly={readonly} />
    case DataCollectionCategories.personalSituation:
      return <PersonalSituation employee={employeeData} readonly={readonly} />
    case DataCollectionCategories.generalInformation:
      return <GeneralBlock employee={employeeData} readonly={readonly} />
    case DataCollectionCategories.identificationNumber:
      return (
        <Identification
          employee={employeeData}
          readonly={readonly}
          payroll={employeeData.payrollable}
          isCurrent={isCurrent}
        />
      )
    case DataCollectionCategories.employee_questions_cf:
      return (
        <CustomFieldsForm
          boxed
          customFieldsOnly
          customResourceFieldsData={filterCustomResourceFieldsDataByUsageGroup(
            customResourceFieldsData,
            'employees-questions'
          )}
          customValuesData={customValuesData}
          padFormDirection="vertical"
          valuableId={employee.id}
          valuableType="Employee"
          currency={employee?.attributes.legal_entity_currency}
        />
      )
    default:
      return null
  }
}

export default EmployeeData
